import { Injectable } from '@angular/core';
import { LoggerService } from './logger.service';
import { ConfirmationService, MessageService } from 'primeng/api';
import { AppBreadcrumbService } from '../../components';
import { MessageType } from '../enums/message-type';
import * as moment from 'moment-timezone';
import { DatePipe } from '@angular/common';
import { AppContentEn } from '..';
import categories from 'src/assets/categories.json';
import { SeoLanguage, SeoLanguageFlat } from '../models/dataforseo/language';
import { Category } from '../models/category';
import { DateRangeOptions } from 'src/app/pages/seo/models/date-range-options';
import { ActivatedRoute } from '@angular/router';
import timezones from '../../../assets/timezone/timezone.json';
import * as CryptoJS from 'crypto-js';

@Injectable({
  providedIn: 'root'
})
export class UtilsService {

    constructor(
        public logger: LoggerService,
        public messageService: MessageService,
        public breadcrumbs: AppBreadcrumbService,
        public appContent: AppContentEn,
        public confirmationService: ConfirmationService,
        public route: ActivatedRoute
    ) {}

    getTimeFromDate(value: string) {

    }

    successMessage(key: MessageType = MessageType.TOAST, title: string, message: string = '', closable: boolean = true, life: number = 6000, sticky: boolean = false) {
        this.messageService.clear();
        this.messageService.add({
            key: key.toString(),
            severity: 'success',
            summary: title,
            detail: message,
            life: life,
            sticky: sticky,
            styleClass:'custom-success',
            closable: closable
        });
    }

    infoMessage(key: MessageType = MessageType.TOAST, title: string, message: string = '', closable: boolean = true, life: number = 20000, sticky: boolean = false) {
        this.messageService.clear();
        this.messageService.add({
            key: key.toString(),
            severity: 'info',
            summary: title,
            detail: message,
            life: life,
            sticky: sticky,
            styleClass:'custom-info',
            closable: closable
        });
    }

    warnMessage(key: MessageType = MessageType.TOAST, title: string, message: string = '', closable: boolean = true, life: number = 20000, sticky: boolean = false) {
        this.messageService.clear();
        this.messageService.add({
            key: key.toString(),
            severity: 'warn',
            summary: title,
            detail: message,
            life: life,
            sticky: sticky,
            styleClass:'custom-warning',
            closable: closable
        });
    }

    errorMessage(key: MessageType = MessageType.TOAST, title: string, message: string = '', closable: boolean = true, life: number = 20000, sticky: boolean = false) {
        this.messageService.clear();
        this.messageService.add({
            key: key.toString(),
            severity: 'error',
            summary: title,
            detail: message,
            life: life,
            sticky: sticky,
            styleClass:'custom-error',
            closable: closable
        });
    }

    formatTime(date: string) {
        let time = new Date(date).toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' }).replace(' ', '');
        if (time.charAt(0) === '0') {
            time = time.substring(1);
        }
        return time;
    }

    confirm(title: string, message: string, accept: Function, reject: Function = null, props: any = null) {
        this.confirmationService.confirm({
            header: title,
            message: message,
            accept: accept,
            reject: reject || (() => { }),
            ...props
        });
    }

    public formatDate(dateStr: string | Date, formatter: string = 'medium') {
        //console.log(`UtilsService.formatDate() - ${dateStr}`);
        const datePipe = new DatePipe('en-US');
        return datePipe.transform(dateStr, formatter);
    }

    toLocalDate(date: string | Date) {
        return moment.utc(date).local().format();
    }

    convertDate(date: Date, tz: string = 'America/New_York') {
        return moment.tz(date, tz).format();
    }

    filterSeoLanguages(dataforseoLanguages: any, locationCodes: any[]): SeoLanguageFlat[] {
        let filteredLanguages = [];
        const languages = this.getSeoLanguagesFlat(dataforseoLanguages);

        locationCodes.map(l => {
            const filteredLanguage = languages.filter((f: SeoLanguageFlat) => {
            return f.locationCode == l.location && f.languageCode == l.language;
        });

            filteredLanguages.push(filteredLanguage);

        });

        return filteredLanguages.reduce((acc, val) => acc.concat(val), []);
    }

    filterSeoLanguagesFlat(dataforseoLanguages: any, locationCodes: any[]): SeoLanguageFlat[] {
        let filteredLanguages = [];
        const languages = this.getSeoLanguagesFlat(dataforseoLanguages);

        locationCodes.map(l => {
            const filteredLanguage = languages.filter((f: SeoLanguageFlat) => {
                return f.locationCode == l.location && f.languageCode == l.language;
            });

            filteredLanguages.push(filteredLanguage);

        });

        return filteredLanguages.reduce((acc, val) => acc.concat(val), []);
    }

    getSeoLanguagesFlat(dataforseoLanguages): SeoLanguageFlat[] {

        const languages: SeoLanguageFlat[] = [];

        dataforseoLanguages.forEach(result => {
            result.availableLanguages.forEach((lan: SeoLanguageFlat) => {
                lan.locationCode = result.locationCode;
                lan.locationName = result.locationName;
                languages.push(lan);
            });
        });

        return languages;
    }

    getCategories(): Category[] {
        return categories;
    }

    //strip host from url which includes http:// or https://
    stripHost(url: string) {
        if (!url) return '';
        return url.replace(/.*?:\/\//g, '');
    }

    //strip host from url which includes http:// or https://
    stripHostAndWWW(url: string) {
        if (!url) return '';
        return url.replace(/^(https?:\/\/)?(www\.)?/g, '');
    }

    getPathFromUrl(urlString) {
        try {
            // Create a new URL object from the input string
            const url = new URL(urlString);

            // Return the path component of the URL
            return url.pathname;
        } catch (error) {
            // Handle invalid URLs or other errors
            console.error(error);
            return '';
        }
    }

    getUrlWithoutPath(urlString) {
        try {
            // Create a new URL object from the input string
            const url = new URL(urlString);

            // Construct and return the URL without the path component
            return `${url.protocol}//${url.hostname}${url.port ? ':' + url.port : ''}`;
        } catch (error) {
            // Handle invalid URLs or other errors
            console.error(error);
            return '';
        }
    }

    public containsProhibitedDomainCharacters(url: string): boolean {
        try {
            // Parse the input as a URL
            const parsedURL = new URL(url.includes("http") ? url : `https://${url}`);

            // Extract the hostname (e.g., walmart.com) for validation
            const hostname = parsedURL.hostname;

            // Regular expression to detect prohibited characters in the hostname
            const prohibitedCharacterRegex = /[!@#$%^&*(),.?":{}|<>]/;

            // Return false if the hostname contains prohibited characters
            return prohibitedCharacterRegex.test(hostname);
        } catch {
            // Return false for invalid inputs that can't be parsed as URLs or domains
            return true;
        }
    }

    public escapeStringForCSV(paragraph: string): string {
        // Escape double quotes with double quotes (CSV standard)
        const escapedParagraph = paragraph.replace(/"/g, '""');

        // If the paragraph contains line breaks, enclose it in double quotes
        if (escapedParagraph.includes('\n')) {
            return `"${escapedParagraph}"`;
        }

        // If the paragraph contains commas, enclose it in double quotes
        if (escapedParagraph.includes(',')) {
            return `"${escapedParagraph}"`;
        }

        return escapedParagraph;
    }

    public exportCSVFile(headers, items, fileTitle) {
        const data = JSON.parse(JSON.stringify(items));
        if (headers) {
            data.unshift(headers);
        }
        // Convert Object to JSON
        var jsonObject = JSON.stringify(data);

        var csv = this.convertToCSV(jsonObject);

        var exportedFilenmae = fileTitle + '.csv' || 'export.csv';

        var blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
        var link = document.createElement("a");
        if (link.download !== undefined) { // feature detection
            // Browsers that support HTML5 download attribute
            var url = URL.createObjectURL(blob);
            link.setAttribute("href", url);
            link.setAttribute("download", exportedFilenmae);
            link.style.visibility = 'hidden';
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
        }
    }

    private convertToCSV(objArray) {
        var array = typeof objArray != 'object' ? JSON.parse(objArray) : objArray;
        var str = '';

        for (var i = 0; i < array.length; i++) {
        var line = '';
        for (var index in array[i]) {
            if (line != '') line += ','

            line += array[i][index];
        }

        str += line + '\r\n';
        }

        return str;
    }

    //Validates that http:// or https:// or www. is included in the target
    public httpAndWWWFound(target: string): boolean {
        const urlRegex = /^https?:\/\/www\.[\w.]+$/;
        return urlRegex.test(target);
    }

    //Validates that http:// or https:// or www. is not included in the target
    public httpWWWNotFound(target: string) {
        const regex = /^(?!https?:\/\/|www\.).*$/;
        return regex.test(target);
    }

    public extractErrorMessages(error) {
        let errorMessages = '';

        if (error.status === 500) {
            errorMessages = 'Unknown error. Please try again later or contact support.';
        }
        else if (error && error.errors) {
            const errors = error.errors || {};
            for (let field in errors) {
                for (let message of errors[field]) {
                errorMessages += `${message}\n`;
                }
            }
        } else if (error && error.detail) {
            errorMessages = error.detail;
        } else {
            errorMessages = 'Unknown error. Please try again later or contact support.';
        }

        return errorMessages.trim();
    }

    public getDateRange(range: DateRangeOptions) {
        const today = new Date();
        let dateFrom, dateTo;
        const firstDayOfMonth = new Date(today.getFullYear(), today.getMonth(), 1);

        switch (range) {
        case DateRangeOptions.Last30Days:
            dateFrom = new Date(today.getFullYear(), today.getMonth(), today.getDate() - 30);
            dateTo = new Date(today.getFullYear(), today.getMonth(), today.getDate());
            break;
        case DateRangeOptions.Last90Days:
            dateFrom = new Date(today.getFullYear(), today.getMonth(), today.getDate() - 90);
            dateTo = new Date(today.getFullYear(), today.getMonth(), today.getDate());
            break;
        case DateRangeOptions.Last6Months:
            dateFrom = new Date(today.getFullYear(), today.getMonth() - 6, firstDayOfMonth.getDate());
            dateTo = new Date(today.getFullYear(), today.getMonth(), firstDayOfMonth.getDate());
            break;
        case DateRangeOptions.Last12Months:
            dateFrom = new Date(today.getFullYear() - 1, today.getMonth(), firstDayOfMonth.getDate());
            dateTo = new Date(today.getFullYear(), today.getMonth(), firstDayOfMonth.getDate());
            break;
        case DateRangeOptions.Last24Months:
            dateFrom = new Date(today.getFullYear() - 2, today.getMonth(), firstDayOfMonth.getDate());
            dateTo = new Date(today.getFullYear(), today.getMonth(), firstDayOfMonth.getDate());
            break;
        case DateRangeOptions.Last36Months:
            dateFrom = new Date(today.getFullYear() - 3, today.getMonth(), firstDayOfMonth.getDate());
            dateTo = new Date(today.getFullYear(), today.getMonth(), firstDayOfMonth.getDate());
            break;
        case DateRangeOptions.Last48Months:
            dateFrom = new Date(today.getFullYear() - 4, today.getMonth(), firstDayOfMonth.getDate());
            dateTo = new Date(today.getFullYear(), today.getMonth(), firstDayOfMonth.getDate());
            break;
        case DateRangeOptions.AllTime:
            dateFrom = new Date(2020, 0, firstDayOfMonth.getDate());
            dateTo = new Date(today.getFullYear(), today.getMonth(), firstDayOfMonth.getDate());
            break;
        default:
            dateFrom = new Date(2020, 0, firstDayOfMonth.getDate());
            dateTo = new Date(today.getFullYear(), today.getMonth(), firstDayOfMonth.getDate());
            break;
        }

        return {
            dateFrom: dateFrom.toISOString().slice(0, 10),
            dateTo: dateTo.toISOString().slice(0, 10)
        };
    }

    /**
     * Stable monthly window for historical APIs (e.g., DataForSEO historical search volume/rank overview).
     * The range is anchored to the last completed month so the payload stays identical for the entire month,
     * which keeps your backend cache key stable until the calendar month rolls over.
     *
     * Example for Nov 15, 2025 and Last12Months:
     *  - dateTo: 2025-10-31 (end of previous month)
     *  - dateFrom: 2024-11-01 (first day of month 11 months before dateTo)
     *  - cacheBucket: "2025-10"
     */
    public getStableHistoricalRange(range: DateRangeOptions): { dateFrom: string; dateTo: string; cacheBucket: string } {
        // Determine months span for a few common ranges. These are full-month buckets.
        const monthsBack = (() => {
            switch (range) {
                case DateRangeOptions.Last6Months: return 6;
                case DateRangeOptions.Last12Months: return 12;
                case DateRangeOptions.Last24Months: return 24;
                case DateRangeOptions.Last36Months: return 36;
                case DateRangeOptions.Last48Months: return 48;
                case DateRangeOptions.Last90Days: return 3;   // approx 3 months
                case DateRangeOptions.Last30Days: return 1;   // approx 1 month
                case DateRangeOptions.AllTime: return 0;
                default: return 12; // sensible default
            }
        })();

        // End at the last completed month (UTC)
        const now = new Date();
        const endOfPrevMonth = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), 0)); // day 0 -> last day of previous month

        // Start at the first day of the month N-1 months before endOfPrevMonth (so total months = monthsBack)
        let startDateUTC: Date;
        if (range === DateRangeOptions.AllTime) {
            startDateUTC = new Date(Date.UTC(2020, 0, 1));
        } else {
            const startYear = endOfPrevMonth.getUTCFullYear();
            const startMonthIndex = endOfPrevMonth.getUTCMonth() - (monthsBack - 1);
            startDateUTC = new Date(Date.UTC(startYear, startMonthIndex, 1));
        }

        const yyyy = endOfPrevMonth.getUTCFullYear();
        const mm = String(endOfPrevMonth.getUTCMonth() + 1).padStart(2, '0');
        const cacheBucket = `${yyyy}-${mm}`; // stable for the entire month

        return {
            dateFrom: startDateUTC.toISOString().slice(0, 10),
            dateTo: endOfPrevMonth.toISOString().slice(0, 10),
            cacheBucket
        };
    }

    /**
     * Same as getStableHistoricalRange but returns only the dates for drop-in usage.
     * Prefer getStableHistoricalRange to also receive cacheBucket for explicit cache scoping.
     */
    public getStableHistoricalDates(range: DateRangeOptions): { dateFrom: string; dateTo: string } {
        const { dateFrom, dateTo } = this.getStableHistoricalRange(range);
        return { dateFrom, dateTo };
    }

    public groupArrayBy(array: any[], key: keyof any): Record<string, any[]> {
        return array.reduce((acc: Record<string, any[]>, obj: any) => {
            const groupKey = obj[key];
            if (acc[groupKey]) {
                acc[groupKey].push(obj);
            } else {
                acc[groupKey] = [obj];
            }
            return acc;
        }, {});
    }

    public parseJwt (token) {
        if (!token) {
            return null;
        }
        var base64Url = token?.split('.')[1];
        var base64 = base64Url?.replace(/-/g, '+').replace(/_/g, '/');
        var jsonPayload = decodeURIComponent(window?.atob(base64).split('').map(function(c) {
            return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2);
        }).join(''));

        return JSON.parse(jsonPayload);
    }

    public get chartColors(): any {
        return [
            { hex: '#873EFE', rgba: 'rgba(135, 62, 254, 1)' },
            { hex: '#44486D', rgba: 'rgba(68, 72, 109, 1)' },
            { hex: '#FFF73D', rgba: 'rgba(255, 247, 61, 1)' },
            { hex: '#5E8065', rgba: 'rgba(94, 128, 101, 1)' },
            { hex: '#3D70FF', rgba: 'rgba(61, 112, 255, 1)' },
            { hex: '#FFA73D', rgba: 'rgba(252, 167, 61, 1)' },
            { hex: '#EC4DBC', rgba: 'rgba(236, 77, 188, 1)' },
            { hex: '#FFA900', rgba: 'rgba(255, 169, 0, 1)' },
            { hex: '#0DC7A3', rgba: 'rgba(13, 199, 163, 1)' },
            { hex: '#9B51E0', rgba: 'rgba(155, 81, 224, 1)' },
            { hex: '#FF407B', rgba: 'rgba(255, 64, 123, 1)' },
            { hex: '#7CDDCF', rgba: 'rgba(124, 221, 207, 1)' }
        ];
    }

    public get transparentChartColors(): any {
        return [
            { hex: '#873EFE', rgba: 'rgba(135, 62, 254, 1)' },
            { hex: '#44486D', rgba: 'rgba(68, 72, 109, 1)' },
            { hex: '#FFF73D', rgba: 'rgba(255, 247, 61, 1)' },
            { hex: '#5E8065', rgba: 'rgba(94, 128, 101, 1)' },
            { hex: '#3D70FF', rgba: 'rgba(61, 112, 255, 1)' },
            { hex: '#FFA73D', rgba: 'rgba(252, 167, 61, 1)' },
            { hex: '#EC4DBC', rgba: 'rgba(236, 77, 188, 1)' },
            { hex: '#FFA900', rgba: 'rgba(255, 169, 0, 1)' },
            { hex: '#0DC7A3', rgba: 'rgba(13, 199, 163, 1)' },
            { hex: '#9B51E0', rgba: 'rgba(155, 81, 224, 1)' },
            { hex: '#FF407B', rgba: 'rgba(255, 64, 123, 1)' },
            { hex: '#7CDDCF', rgba: 'rgba(124, 221, 207, 1)' }
        ];
    }

    public get chartColorsHex(): string[] {
        const hexValues: string[] = [];
        for (const color of this.chartColors) {
            hexValues.push(color.hex);
        }
        return hexValues;
    }

    // Extract domain from URL
    public extractDomain(url: string): string {
        const domainRegex = /^(?:https?:\/\/)?(?:[^@\n]+@)?(?:www\.)?([^:\/\n]+)/;
        const matches = url.match(domainRegex);

        if (matches && matches.length > 1) {
          return matches[1];
        } else {
          return '';
        }
    }

    public exportCSVFileErrorMessage() {
        return {
            header: 'Unable to Export',
            message: 'No data available to export.'
        };
    }

    public asinExtractor(url: string): string {
        const asnMatch = url.match(/https:\/\/www\.amazon\.com\/(?:.+\/dp\/|dp\/)([A-Z0-9]{10})(?:\/|$)/i);
        if (asnMatch) {
            return asnMatch[1];
        } else {
            return url;
        }
    }
    public getTimezones() {
        return timezones;
    }

    // Utility function to hash the payload
    public hashPayload(payload: any): string {
      return CryptoJS.SHA256(JSON.stringify(payload)).toString(CryptoJS.enc.Hex);
    }

    /**
     * Parses API validation error response into a flat list of readable messages.
     * @param errorResponse - The error object returned from the API.
     * @returns Array of user-friendly error messages.
     */
    private parseValidationErrors(errorResponse: unknown, fallback: string): string[] {

        if (typeof errorResponse !== 'object' || errorResponse === null) return [fallback];
        const payload = errorResponse as Record<string, unknown>;

        if (!payload['errors'] || typeof payload['errors'] !== 'object') {

            if (typeof payload['detail'] === 'string' && payload['detail'].trim()) {
                return [payload['detail']];
            }
            else if (typeof payload['title'] === 'string' && payload['title'].trim()) {
                return [payload['title']];
            }
            else if (typeof payload['message'] === 'string' && payload['message'].trim()) {
                const messages: string[] = [payload['message']];
                if (Array.isArray(payload['organizations'])) {
                    const organizations: string[] = payload['organizations']
                        .filter((organization: unknown): organization is { id: number; name: string } =>
                            typeof organization === 'object' && organization !== null &&
                            typeof (organization as { name?: unknown }).name === 'string' &&
                            typeof (organization as { id?: unknown }).id === 'number')
                        .map((organization: { id: number; name: string }) =>
                            `${organization.name} (ID: ${organization.id})`);
                    if (organizations.length > 0) messages.push(`Workspaces: ${organizations.join('; ')}`);
                }
                return messages;
            }
            return [fallback];
        }

        const messages: string[] = [];

        for (const [key, value] of Object.entries(payload['errors'])) {
            const formattedKey = key
            .replace(/\[(\d+)\]/g, ' #$1')   // turn [0] → #0
            .replace(/\./g, ' → ')           // replace dot with arrow
            .replace(/([A-Z])/g, ' $1')      // add space before capital letters
            .trim();

            (Array.isArray(value) ? value.filter((item): item is string => typeof item === 'string') : []).forEach(msg => {
                messages.push(`${formattedKey}: ${msg}`);
            });
        }

        return messages.length > 0 ? messages : [fallback];
    }

    /**
     * Handles backend API errors and displays formatted messages.
     * @param response - The API error response object.
     */
    public handleApiError(response: any, msg: string = 'Unknown error occurred.'): void {

        let messages: string[] = [];

        if (!response) {
            messages = [msg];
        } else if (response?.status === 500) {
            messages = [msg];
        } else if (response?.error) {
            messages = this.parseValidationErrors(response.error, msg);
        } else if (response?.message) {
            messages = [response.message];
        } else {
            messages = [msg];
        }

        // Log for debugging
        this.logger.error('API Error:', response);

        // Display as toast (PrimeNG)
        this.errorMessage(
            MessageType.TOAST,
            'Error',
            messages.join('\n'),
            true,
            10000
        );
    }

}
